import { Component } from '@angular/core';
import { Address } from '@models/address';
import { MatterFileType, TITLE_SEARCH_MATTER_STATE } from '@models/matter';
import { Store } from '@ngxs/store';
import { CommissionSelectors } from '@state/global/commission';
import { MatterClients } from '@state/global/matter_clients';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { map, filter, startWith } from 'rxjs/operators';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { environment } from 'src/environments/environment';
import { MatterDispatch } from '@state/matter';
import { TestHooks } from './summary.hooks';
import { UrlService } from '@services/url/url.service';
import { combineLatest, fromEvent } from 'rxjs';
import { getNameFull } from '@core/util/client';
import { employeeName } from '@core/util/company';
import { BusinessFeatureFlagSelectors, BusinessState } from '@state/business';
import { getEmployeePhoneNumber } from '@core/util/employee';
import { APS_SIDE_FORM_FRAGMENTS } from '../../sale/aps/aps-side-form/aps-side-form.component';
import { COMMISSION_DETAILS_FRAGMENT } from '../../sale/commission/commission-details/commission-details.component';

@Component({
	selector: 'app-summary',
	templateUrl: './summary.component.html',
	styles: [
		`
			:host {
				@apply bg-gray-50 overflow-y-auto p-8 grid grid-cols-12 gap-4 shadow-inner w-full h-full;
			}
		`,
	],
	standalone: false,
})
export class CreateMatterSummaryComponent {
	testHooks = TestHooks;
	private matterId$ = this.store.select(SelectedMatter.id);
	public fieldMappings$ = this.store.select(SelectedMatter.getFieldMappings);
	public localAddress$ = this.store.select(SelectedMatter.address);

	public address$ = this.localAddress$;

	public locationMapUrl$ = combineLatest([
		this.address$.pipe(filter((address) => !!address)),
		fromEvent(window, 'resize').pipe(startWith('')),
	]).pipe(map(([address]) => this.getMapUrl(address)));

	public locationMapLink$ = this.address$.pipe(
		filter((address) => !!address),
		map((address) => this.getMapLink(address)),
	);

	public clientBrokerage$ = this.store.select(SelectedMatter.clientBrokerage);

	public CreateMatterAPS_URL$ = this.matterId$.pipe(
		map((id) => UrlService.CreateMatter_APS(id)),
	);

	public MatterTitleSearchURL$ = this.matterId$.pipe(
		map((id) => UrlService.Matter_TitleSearch(id)),
	);

	public MatterPartiesClients$ = this.matterId$.pipe(
		map((id) => UrlService.Matter_Parties(id)),
	);

	public propertyTypeLabel$ = this.store.select(
		SelectedMatter.propertyTypeLabel,
	);

	public propertyTypeStatus$ = this.store
		.select(SelectedMatter.propertyType)
		.pipe(map((propertyType) => propertyType?.status));

	public hasDoneAPSAiScraping$ = this.store.select(
		SelectedMatter.hasDoneAPSAiScraping,
	);

	public isClientIntakeEnabled$ = this.store.select(
		BusinessFeatureFlagSelectors.isClientIntakeEnabled,
	);

	public quickContacts$ = combineLatest([
		this.clientBrokerage$,
		this.store.select(SelectedMatter.otherPartyLawFirm),
		this.store.select(SelectedMatter.matterFileType),
	]).pipe(
		map(([clientBrokerage, otherPartyLawFirm, fileType]) => {
			const [clientTitle, otherPartyTitle, clientFragment] = {
				[MatterFileType.SALE]: [
					'Vendor',
					'Purchaser',
					APS_SIDE_FORM_FRAGMENTS.SELLER_BROKERAGE,
				],
				[MatterFileType.PURCHASE]: [
					'Purchaser',
					'Vendor',
					APS_SIDE_FORM_FRAGMENTS.BUYER_BROKERAGE,
				],
			}[fileType] ?? [
				'Client',
				'Other Party',
				APS_SIDE_FORM_FRAGMENTS.SELLER_BROKERAGE,
			];
			return [
				{
					cardName: 'Client Contacts',
					fragment: clientFragment,
					information: [
						{
							icon: 'building',
							title: `${clientTitle}'s Brokerage`,
							name: clientBrokerage?.name,
							phone: clientBrokerage?.phoneNumber,
							email: clientBrokerage?.email,
						},
						{
							icon: 'user',
							title: `${clientTitle}'s Agent`,
							name: employeeName(
								clientBrokerage?.broker,
								this.store.selectSnapshot(
									BusinessState.getNameFormattingSettings,
								),
							),
							email: clientBrokerage?.broker?.email,
							phone: getEmployeePhoneNumber(clientBrokerage?.broker),
						},
					],
				},
				{
					cardName: 'Other Party Contacts',
					fragment: APS_SIDE_FORM_FRAGMENTS.OTHER_LAWYER,
					information: [
						{
							icon: 'building',
							title: `${otherPartyTitle}'s Law Firm`,
							name: otherPartyLawFirm?.name,
							phone: otherPartyLawFirm?.phoneNumber,
							email: otherPartyLawFirm?.email,
						},
						{
							icon: 'user',
							title: `${otherPartyTitle}'s Lawyer`,
							name: employeeName(
								otherPartyLawFirm?.lawyer,
								this.store.selectSnapshot(
									BusinessState.getNameFormattingSettings,
								),
							),
							email: otherPartyLawFirm?.lawyer?.email,
							phone: getEmployeePhoneNumber(otherPartyLawFirm?.lawyer),
						},
					],
				},
			];
		}),
	);

	constructor(
		private navService: NavigationService,
		private matterDispatch: MatterDispatch,
		private store: Store,
	) {}

	goToTitleSearch(): void {
		this.matterDispatch.OverwriteFields(
			'state.titleSearch',
			TITLE_SEARCH_MATTER_STATE.SEARCH,
		);

		this.navService.goToMatter_TitleSearch(
			this.store.selectSnapshot(SelectedMatter.id),
		);
	}

	private otherPartyDetails$ = combineLatest([
		this.store.select(SelectedMatter.otherPartyBrokerage),
		this.store.select(MatterClients.get_otherParties),
		this.store.select(SelectedMatter.matterFileType),
	]).pipe(
		map(([brokerage, otherParties, matterFileType]) => {
			const otherPartyNames = otherParties
				.map((client) => getNameFull(client))
				.join(', ');
			const agentFullName = employeeName(
				brokerage?.broker,
				this.store.selectSnapshot(BusinessState.getNameFormattingSettings),
			);

			switch (matterFileType) {
				case MatterFileType.PURCHASE:
					return [
						{
							title: otherParties.length > 1 ? 'Vendors' : 'Vendor',
							value: otherPartyNames,
							fragment: APS_SIDE_FORM_FRAGMENTS.CLIENTS,
						},
						{
							title: "Vendor's Brokerage",
							value: brokerage?.name,
							fragment: APS_SIDE_FORM_FRAGMENTS.SELLER_AGENT_BROKERAGE,
						},
						{
							title: "Vendor's Agent",
							value: agentFullName,
							fragment: APS_SIDE_FORM_FRAGMENTS.SELLER_AGENT_BROKERAGE,
						},
					];
				case MatterFileType.SALE:
					return [
						{
							title: otherParties.length > 1 ? 'Purchasers' : 'Purchaser',
							value: otherPartyNames,
							fragment: APS_SIDE_FORM_FRAGMENTS.CLIENTS,
						},
						{
							title: "Purchaser's Brokerage",
							value: brokerage?.name,
							fragment: APS_SIDE_FORM_FRAGMENTS.BUYER_BROKERAGE,
						},
						{
							title: "Purchaser's Agent",
							value: agentFullName,
							fragment: APS_SIDE_FORM_FRAGMENTS.BUYER_BROKERAGE,
						},
					];
			}
		}),
	);

	private clientDetails$ = combineLatest([
		this.clientBrokerage$,
		this.store.select(MatterClients.get_clients),
		this.store.select(SelectedMatter.matterFileType),
	]).pipe(
		map(([brokerage, clients, matterFileType]) => {
			const clientNames = clients
				.map((client) => getNameFull(client))
				.join(', ');
			const agentFullName = employeeName(
				brokerage?.broker,
				this.store.selectSnapshot(BusinessState.getNameFormattingSettings),
			);

			switch (matterFileType) {
				case MatterFileType.SALE:
					return [
						{
							title: clients.length > 1 ? 'Vendors' : 'Vendor',
							value: clientNames,
							fragment: APS_SIDE_FORM_FRAGMENTS.CLIENTS,
						},
						{
							title: "Vendor's Brokerage",
							value: brokerage?.name,
							fragment: APS_SIDE_FORM_FRAGMENTS.SELLER_BROKERAGE,
						},
						{
							title: "Vendor's Agent",
							value: agentFullName,
							fragment: APS_SIDE_FORM_FRAGMENTS.SELLER_BROKERAGE,
						},
					];
				case MatterFileType.PURCHASE:
					return [
						{
							title: clients.length > 1 ? 'Purchasers' : 'Purchaser',
							value: clientNames,
							fragment: APS_SIDE_FORM_FRAGMENTS.CLIENTS,
						},
						{
							title: "Purchaser's Brokerage",
							value: brokerage?.name,
							fragment: APS_SIDE_FORM_FRAGMENTS.BUYER_BROKERAGE,
						},
						{
							title: "Purchaser's Agent",
							value: agentFullName,
							fragment: APS_SIDE_FORM_FRAGMENTS.BUYER_BROKERAGE,
						},
					];
			}
		}),
	);

	private keyDates$ = this.store.select(SelectedMatter.keyDates).pipe(
		map((keyDates) => [
			{
				title: 'Agreement Date',
				value: keyDates.agreementDate,
				fragment: APS_SIDE_FORM_FRAGMENTS.AGREEMENT_DATE,
			},
			{
				title: 'Closing Date',
				value: keyDates.closingDate,
				fragment: APS_SIDE_FORM_FRAGMENTS.KEY_DATES,
			},
			{
				title: 'Requisition Date',
				value: keyDates.titleSearchCompletionDate,
				fragment: APS_SIDE_FORM_FRAGMENTS.KEY_DATES,
			},
		]),
	);

	private financials$ = combineLatest([
		this.store.select(SelectedMatter.purchasePrice),
		this.store.select(AdjustmentsSelector.depositAdjustmentCalculated),
		this.store.select(CommissionSelectors.vendorCommissionCalculated),
		this.store.select(CommissionSelectors.purchaserCommissionCalculated),
		this.store.select(SelectedMatter.showBuyerBrokerageCreate),
		this.store.select(SelectedMatter.matterFileType),
	]).pipe(
		map(
			([
				purchasePrice,
				deposit,
				vendorCommission,
				buyerCommission,
				showBuyerBrokerage,
				matterFileType,
			]) => {
				const financials: {
					title: string;
					value: number;
					fragment: string;
					href?: string;
				}[] = [
					{
						title: 'Purchase Price',
						value: purchasePrice,
						fragment: APS_SIDE_FORM_FRAGMENTS.FINANCIALS,
					},
					{
						title: 'Deposit',
						value: deposit?.absAmount,
						fragment: APS_SIDE_FORM_FRAGMENTS.FINANCIALS,
					},
				];

				if (matterFileType === MatterFileType.SALE) {
					financials.push({
						title: showBuyerBrokerage ? "Vendor's Commission" : 'Commission',
						value: vendorCommission?.grandTotal,
						fragment: COMMISSION_DETAILS_FRAGMENT.DETAILS,
						href: UrlService.CreateMatter_Commission_Seller_Brokerage(
							this.store.selectSnapshot(SelectedMatter.id),
						),
					});
				}

				if (showBuyerBrokerage) {
					financials.push({
						title:
							matterFileType === MatterFileType.SALE
								? "Buyer's Commission"
								: 'Commission',
						value: buyerCommission?.grandTotal,
						fragment: COMMISSION_DETAILS_FRAGMENT.DETAILS,
						href: UrlService.CreateMatter_Commission_Buyer_Brokerage(
							this.store.selectSnapshot(SelectedMatter.id),
						),
					});
				}

				return financials;
			},
		),
	);

	public infoCards = [
		{
			infoArray$: this.keyDates$,
			usePipe: 'date',
			title: 'Key Dates',
		},
		{
			infoArray$: this.financials$,
			usePipe: 'centsToDollars',
			title: 'Financials',
		},
		{
			infoArray$: this.clientDetails$,
			title: 'Client Details',
		},
		{
			infoArray$: this.otherPartyDetails$,
			title: 'Other Party Details',
		},
	];

	private getMapUrl(_add: Address) {
		const base = `https://maps.googleapis.com/maps/api/staticmap?center=${_add.street}%2C${_add.city}%2C${_add.province}%2C${_add.country}%2C${_add.postcode}&markers=color:red`;
		const imageHeight = Math.round(window.innerHeight * 0.3);
		const imageWidth = Math.round(imageHeight * (16 / 9));
		return _add?._geoloc?.lat
			? `${base}%7C${_add?._geoloc?.lat},${_add?._geoloc?.lng}&size=${imageWidth}x${imageHeight}&zoom=17&key=${environment.firebase.apiKey}`
			: base;
	}

	private getMapLink(address: Address) {
		if (
			!address.country &&
			!address.city &&
			!address.province &&
			!address.street
		)
			return '';
		const query = [
			address.street,
			address.city,
			address.province,
			address.country,
			address.postcode,
		].filter(Boolean);
		if (query.length !== 5) return '';
		return `https://www.google.com/maps/search/?api=1&query=${query.join('%2C')}`;
	}
}
